/**
 * 커미션 관리 API 서비스
 * 
 * 백엔드 API 엔드포인트:
 * - GET /api/commissions?year=2026&month=1
 * - POST /api/commissions
 * - PUT /api/commissions/:id
 * - DELETE /api/commissions/:id
 */

import api from './api';

/**
 * 월별 커미션 목록 조회
 * @param {number} year - 년도
 * @param {number} month - 월 (1-12)
 * @returns {Promise<{commissions: Array, total_amount: number, count: number}>}
 */
export const getCommissions = async (year, month) => {
    const response = await api.get('/commissions', { year, month });
    return response.data;
};

export const getCommissionAccountingExport = async (year, month) => {
    const response = await api.get('/commissions/accounting-export', { year, month });
    return response.data;
};

/**
 * 커미션 추가
 * @param {Object} data - 커미션 데이터
 * @param {string} data.artist_name - 타투이스트 이름
 * @param {string} data.position - 지위 (resident, guest, staff, other)
 * @param {number} data.amount - 금액
 * @param {string} data.received_date - 받은 날짜 (YYYY-MM-DD)
 * @param {string} data.platform - 플랫폼 (cash, paypal, transfer, other)
 * @param {string} [data.memo] - 메모 (선택)
 * @returns {Promise<Object>} 생성된 커미션
 */
export const createCommission = async (data) => {
    const response = await api.post('/commissions', data);
    return response.data;
};

/**
 * 커미션 수정
 * @param {string} id - 커미션 ID
 * @param {Object} data - 수정할 데이터
 * @returns {Promise<Object>} 수정된 커미션
 */
export const updateCommission = async (id, data) => {
    const response = await api.put(`/commissions/${id}`, data);
    return response.data;
};

/**
 * 커미션 삭제
 * @param {string} id - 커미션 ID
 * @returns {Promise<void>}
 */
export const deleteCommission = async (id) => {
    await api.delete(`/commissions/${id}`);
};

/**
 * 연도별 커미션 합계 조회
 * @param {number} [startYear] - 시작 연도 (선택)
 * @param {number} [endYear] - 종료 연도 (선택)
 * @returns {Promise<Array<{year: number, total_amount: number}>>}
 */
export const getYearlySummary = async (startYear, endYear) => {
    const params = {};
    if (startYear) params.start_year = startYear;
    if (endYear) params.end_year = endYear;
    const response = await api.get('/commissions/yearly-summary', params);
    return response.yearly_summary || [];
};

/**
 * 월별 커미션 합계 조회
 * @param {number} year - 조회할 연도
 * @returns {Promise<Array<{month: number, total_amount: number}>>}
 */
export const getMonthlySummary = async (year) => {
    const response = await api.get('/commissions/monthly-summary', { year });
    return response.monthly_summary || [];
};

const commissionApi = {
    getCommissions,
    getCommissionAccountingExport,
    createCommission,
    updateCommission,
    deleteCommission,
    getYearlySummary,
    getMonthlySummary,
};

export default commissionApi;
